import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Award, Crown, Medal, Star, Sparkles, Palette, Laugh, Newspaper, Mic, BookOpen } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Award category configurations
const CATEGORY_CONFIG = {
  artistic: { icon: Palette, color: "#8b5cf6", emoji: "🎨", name: "Artistic" },
  comedic: { icon: Laugh, color: "#f59e0b", emoji: "😂", name: "Comedic" },
  journalistic: { icon: Newspaper, color: "#3b82f6", emoji: "📰", name: "Journalistic" },
  podcast: { icon: Mic, color: "#ef4444", emoji: "🎙️", name: "Podcast" },
  inspirational: { icon: Sparkles, color: "#10b981", emoji: "✨", name: "Inspirational" },
  educational: { icon: BookOpen, color: "#06b6d4", emoji: "📚", name: "Educational" }
};

// Badge level colors
const LEVEL_COLORS = {
  legendary: { bg: 'from-purple-500 to-pink-500', border: 'border-purple-400', glow: 'shadow-purple-500/50' },
  gold: { bg: 'from-yellow-400 to-amber-500', border: 'border-yellow-400', glow: 'shadow-yellow-500/50' },
  silver: { bg: 'from-gray-300 to-gray-400', border: 'border-gray-400', glow: 'shadow-gray-400/50' },
  bronze: { bg: 'from-orange-400 to-amber-600', border: 'border-orange-400', glow: 'shadow-orange-500/50' }
};

/**
 * AwardBadges - Displays a user's award badges on their profile
 */
const AwardBadges = ({ userId, size = 'md', showAll = false, maxBadges = 4 }) => {
  const [badges, setBadges] = useState([]);
  const [totalAwards, setTotalAwards] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      fetchBadges();
    }
  }, [userId]);

  const fetchBadges = async () => {
    try {
      const response = await axios.get(`${API_URL}/awards/user/${userId}/badges`);
      setBadges(response.data.badges || []);
      setTotalAwards(response.data.total_awards || 0);
    } catch (error) {
      console.log('Could not fetch badges');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return null;
  }

  if (badges.length === 0) {
    return null;
  }

  const displayBadges = showAll ? badges : badges.slice(0, maxBadges);
  const hasMore = !showAll && badges.length > maxBadges;

  const sizes = {
    sm: { badge: 'w-6 h-6', icon: 'text-xs' },
    md: { badge: 'w-8 h-8', icon: 'text-sm' },
    lg: { badge: 'w-10 h-10', icon: 'text-base' }
  };

  const sizeConfig = sizes[size] || sizes.md;

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {displayBadges.map((badge, idx) => {
        const levelConfig = LEVEL_COLORS[badge.level];
        
        return (
          <motion.div
            key={badge.category}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: idx * 0.1, type: 'spring', stiffness: 400 }}
            className="relative group"
            title={`${badge.category_name} Award - ${badge.level_name} (${badge.wins.total} wins)`}
          >
            <div className={`${sizeConfig.badge} rounded-full bg-gradient-to-br ${levelConfig.bg} flex items-center justify-center border-2 ${levelConfig.border} shadow-lg ${levelConfig.glow} cursor-pointer`}>
              <span className={sizeConfig.icon}>{badge.emoji}</span>
            </div>
            
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
              <div className="font-bold">{badge.category_name} Award</div>
              <div className="text-gray-300">{badge.level_name} • {badge.wins.total} wins</div>
              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
            </div>
          </motion.div>
        );
      })}
      
      {hasMore && (
        <Link
          to="/hall-of-fame"
          className={`${sizeConfig.badge} rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors cursor-pointer`}
          title="View all awards"
        >
          <span className={`${sizeConfig.icon} font-semibold`}>+{badges.length - maxBadges}</span>
        </Link>
      )}
      
      {totalAwards > 0 && size !== 'sm' && (
        <Link
          to="/hall-of-fame"
          className="ml-1 flex items-center gap-1 text-yellow-600 hover:text-yellow-700 text-sm font-semibold"
        >
          <Trophy className="w-4 h-4" />
          {totalAwards}
        </Link>
      )}
    </div>
  );
};

/**
 * ProfileAwardSection - Full award section for profile pages
 */
export const ProfileAwardSection = ({ userId }) => {
  const [badges, setBadges] = useState([]);
  const [totalAwards, setTotalAwards] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      fetchBadges();
    }
  }, [userId]);

  const fetchBadges = async () => {
    try {
      const response = await axios.get(`${API_URL}/awards/user/${userId}/badges`);
      setBadges(response.data.badges || []);
      setTotalAwards(response.data.total_awards || 0);
    } catch (error) {
      console.log('Could not fetch badges');
    } finally {
      setLoading(false);
    }
  };

  if (loading || badges.length === 0) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 rounded-2xl p-4 border border-yellow-200 dark:border-yellow-800">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-500" />
          Award Badges
        </h3>
        <Link
          to="/hall-of-fame"
          className="text-sm text-yellow-600 hover:text-yellow-700 font-semibold"
        >
          View Hall of Fame
        </Link>
      </div>
      
      <div className="flex flex-wrap gap-3">
        {badges.map((badge, idx) => {
          const levelConfig = LEVEL_COLORS[badge.level];
          
          return (
            <motion.div
              key={badge.category}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="flex items-center gap-2 bg-white dark:bg-gray-800 rounded-xl px-3 py-2 shadow-sm"
            >
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${levelConfig.bg} flex items-center justify-center border-2 ${levelConfig.border}`}>
                <span className="text-lg">{badge.emoji}</span>
              </div>
              <div>
                <p className="font-semibold text-sm text-gray-900 dark:text-white">
                  {badge.category_name}
                </p>
                <p className="text-xs text-gray-500">
                  {badge.level_name} • {badge.wins.total} wins
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>
      
      <div className="mt-3 pt-3 border-t border-yellow-200 dark:border-yellow-800 flex items-center justify-between">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Total Awards: <span className="font-bold text-yellow-600">{totalAwards}</span>
        </p>
        <Link
          to="/awards"
          className="text-sm text-yellow-600 hover:text-yellow-700 font-semibold"
        >
          Vote for Awards →
        </Link>
      </div>
    </div>
  );
};

export default AwardBadges;
